// File: /var/www/app1/frontend/src/components/AdminDashboard.js

import React, { useState, useEffect } from "react";
import axios from "axios";
import MissionaryForm from "./MissionaryForm";

function AdminDashboard({ user }) {
  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');
  const [workers, setWorkers] = useState([]);
  const [newWorkerNumber, setNewWorkerNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [editingOriginalId, setEditingOriginalId] = useState(null);
  const [editingOriginalValue, setEditingOriginalValue] = useState("");

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const response = await axios.get("/api/missionaries");
      setWorkers(response.data.missionaries || []);
    } catch (error) {
      console.error("Failed to fetch workers:", error);
      setError("Failed to load workers");
    }
  };

  const handleAddWorker = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await axios.post("/api/missionaries", {
        missionary_number: newWorkerNumber,
      });

      if (response.data.success) {
        setSuccess(`Worker ${newWorkerNumber} added successfully!`);
        setNewWorkerNumber("");
        fetchWorkers(); // Refresh the list
      }
    } catch (error) {
      setError(error.response?.data?.error || "Failed to add worker");
    } finally {
      setLoading(false);
    }
  };

  const handleHideWorker = async (worker) => {
    if (!window.confirm(`Are you sure you want to hide worker ${worker.missionary_number}? They will no longer appear on the dashboard.\n\nExisting reports will be preserved.`)) return;
    try {
      await axios.patch(`/api/missionaries/${worker.id}`, { isActive: false });
      setSuccess(`Worker ${worker.missionary_number} hidden successfully`);
      fetchWorkers();
    } catch (error) {
      setError(error.response?.data?.error || "Failed to hide worker");
    }
  };

  const handleStartEditOriginal = (worker) => {
    setEditingOriginalId(worker.id);
    setEditingOriginalValue(worker.old_missionary_number || "");
  };

  const handleSaveOriginal = async (worker) => {
    try {
      await axios.patch(`/api/missionaries/${worker.id}`, {
        old_missionary_number: editingOriginalValue,
      });
      setEditingOriginalId(null);
      fetchWorkers();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update Original #");
    }
  };

  const handleCancelEditOriginal = () => {
    setEditingOriginalId(null);
    setEditingOriginalValue("");
  };

  const handleFillForm = (worker) => {
    setSelectedWorker(worker);
    setEditDraft(null);
    setShowForm(true);
  };

  const handleEditReview = async (worker) => {
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const month = worker.draft_month || currentMonth;
      const year = worker.draft_year || currentYear;

      const response = await axios.get(`/api/drafts/${worker.id}?month=${month}&year=${year}`);
      if (response.data.success) {
        setSelectedWorker(worker);
        setEditDraft(response.data.draft);
        setShowForm(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load draft");
    }
  };

  const handleBackToList = () => {
    setShowForm(false);
    setSelectedWorker(null);
    setEditDraft(null);
    fetchWorkers(); // Refresh to show updated submission dates
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  // If showing form for a specific worker
  if (showForm && selectedWorker) {
    return (
      <div>
        <button
          onClick={handleBackToList}
          style={{
            margin: "20px",
            padding: "10px 20px",
            backgroundColor: "#667eea",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          ← Back to Worker List
        </button>
        <MissionaryForm
          selectedMissionary={selectedWorker}
          initialDraft={editDraft}
          onSuccess={() => {
            setSuccess("Report submitted successfully!");
            handleBackToList();
          }}
          onSaveAndClose={() => {
            setSuccess("Draft saved successfully!");
            handleBackToList();
          }}
        />
      </div>
    );
  }

  // Calculate statistics
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const submittedThisMonth = workers.filter((e) => {
    if (!e.last_submission) return false;
    const submissionDate = new Date(e.last_submission);
    return (
      submissionDate.getMonth() === currentMonth &&
      submissionDate.getFullYear() === currentYear
    );
  }).length;

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>

      {/* Statistics */}
      <div className="admin-section">
        <h3>Statistics</h3>
        <div className="stats-container">
          <div className="stat-card">
            <h4>Total Workers</h4>
            <p className="stat-number">{workers.length}</p>
          </div>
          <div className="stat-card">
            <h4>Submitted This Month</h4>
            <p className="stat-number">
              {submittedThisMonth} / {workers.length}
            </p>
          </div>
          <div className="stat-card">
            <h4>Pending Submissions</h4>
            <p className="stat-number">{workers.length - submittedThisMonth}</p>
          </div>
        </div>
      </div>

      {/* Worker List */}
      <div className="admin-section">
        <h3>Worker List - Click to Fill Form</h3>
        <div className="table-container">
          <table className="missionary-table">
            <thead>
              <tr>
                <th>Worker Number</th>
                <th>Original #</th>
                {isAdmin && <th>Added On</th>}
                <th>Last Submission</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {workers.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="no-data">
                    No workers registered yet
                  </td>
                </tr>
              ) : (
                workers.map((worker) => {
                  const hasSubmittedThisMonth =
                    worker.last_submission &&
                    new Date(worker.last_submission).getMonth() ===
                      currentMonth &&
                    new Date(worker.last_submission).getFullYear() ===
                      currentYear;

                  const hasDraft = !!worker.draft_id;

                  return (
                    <tr key={worker.id}>
                      <td>{worker.missionary_number}</td>
                      <td>
                        {isAdmin && editingOriginalId === worker.id ? (
                          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            <input
                              type="text"
                              value={editingOriginalValue}
                              onChange={(e) => setEditingOriginalValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveOriginal(worker);
                                if (e.key === "Escape") handleCancelEditOriginal();
                              }}
                              autoFocus
                              style={{
                                width: "60px",
                                padding: "2px 4px",
                                border: "1px solid #667eea",
                                borderRadius: "3px",
                                fontSize: "13px",
                              }}
                            />
                            <button
                              onClick={() => handleSaveOriginal(worker)}
                              style={{
                                background: "#48bb78",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={handleCancelEditOriginal}
                              style={{
                                background: "#e53e3e",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                padding: "2px 6px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span
                            onClick={() => isAdmin && handleStartEditOriginal(worker)}
                            style={{
                              cursor: isAdmin ? "pointer" : "default",
                              borderBottom: isAdmin ? "1px dashed #aaa" : "none",
                              minWidth: "30px",
                              display: "inline-block",
                            }}
                            title={isAdmin ? "Click to edit" : ""}
                          >
                            {worker.old_missionary_number || "—"}
                          </span>
                        )}
                      </td>
                      {isAdmin && <td>{formatDate(worker.created_at)}</td>}
                      <td>{formatDate(worker.last_submission)}</td>
                      <td>
                        {hasSubmittedThisMonth ? (
                          <span style={{ color: "green" }}>✓ Submitted</span>
                        ) : hasDraft ? (
                          <span style={{ color: "#ed8936" }}>✎ Draft Saved</span>
                        ) : (
                          <span style={{ color: "orange" }}>⚠ Pending</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          <button
                            onClick={() => handleFillForm(worker)}
                            className="fill-form-btn"
                            style={{
                              backgroundColor: hasSubmittedThisMonth
                                ? "#48bb78"
                                : "#667eea",
                              color: "white",
                              border: "none",
                              padding: "6px 12px",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          >
                            Fill Form
                          </button>
                          {hasDraft && (
                            <button
                              onClick={() => handleEditReview(worker)}
                              style={{
                                backgroundColor: "#ed8936",
                                color: "white",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              Edit & Review
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => handleHideWorker(worker)}
                              style={{
                                backgroundColor: "#e53e3e",
                                color: "white",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: "4px",
                                cursor: "pointer",
                              }}
                            >
                              Hide
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Worker Form - Admin only */}
      {isAdmin && (
        <div className="admin-section">
          <h3>Add New Worker</h3>
          <form onSubmit={handleAddWorker} className="add-missionary-form">
            <input
              type="text"
              value={newWorkerNumber}
              onChange={(e) => setNewWorkerNumber(e.target.value)}
              placeholder="Enter worker number"
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? "Adding..." : "Add Worker"}
            </button>
          </form>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>
      )}

      {!isAdmin && error && <div className="error-message">{error}</div>}
      {!isAdmin && success && <div className="success-message">{success}</div>}
    </div>
  );
}

export default AdminDashboard;
